<template>
    <div ref="adminTabItems" class="tab-items mb-3">
        <EMDataTable ref="surveysTable" :column-def="columnDef" :data-url="'/admin/surveys'" sort="created_at" sort_dir="desc" @data-loaded="helper.loaderOff(adminTabItems)" @loading-data="helper.loaderOn(adminTabItems)">
            <template v-slot:medication_name="{ rowData, column }">
                <td :style="column.width ? 'width: '+column.width+'%' : ''">
                    <router-link v-if="rowData.status !== 'pending'" :class="{'text-info' : !rowData.medication_name || rowData.medication_name === 'Not Set', 'text-dark' : rowData.medication_name && rowData.medication_name !== 'Not Set'}" :to="{name: 'single_survey', params : { 'id' : rowData.uuid}}">{{ rowData.medication_name }}</router-link>
                    <p v-else class="text-dark mb-0" :class="{'text-info' : !rowData.medication_name || rowData.medication_name === 'Not Set', 'text-dark' : rowData.medication_name && rowData.medication_name !== 'Not Set'}">{{ rowData.medication_name }}</p>
                    <p class="m-0 fs-sm text-capitalize" :class="{'text-info' : !rowData.surveyable_name || rowData.surveyable_name === 'Not Set', 'text-muted' : rowData.surveyable_name && rowData.surveyable_name !== 'Not Set'}">{{ rowData.surveyable_name }}</p>
                </td>
            </template>
            <template v-slot:status="{ rowData, column }">
                <td class="text-center" :style="column.width ? 'width: '+column.width+'%' : ''">
                    <span class="text-capitalize badge" :class="{'badge-warning' : rowData.status === 'pending','badge-info' : rowData.status === 'submitted' || rowData.status === 'rejected - new submission', 'badge-success' : rowData.status === 'approved', 'badge-danger' : rowData.status === 'rejected' || rowData.status === 'failed', 'badge-dark' : 'processing'}">{{ rowData.status }}</span>
                </td>
            </template>
            <template v-slot:action="{ rowData, column }">
                <td :style="column.width ? 'width: '+column.width+'%' : ''">
                    <div v-if="rowData.status !== 'pending'"  class="d-flex gap-2 justify-content-end">

                        <i v-if="rowData.status === 'submitted' || rowData.status === 'rejected' || rowData.status === 'rejected - new submission' || rowData.status === 'failed'" class="bi bi-check2-circle text-success hoverable-icon" v-tooltip="'Approve this Survey'"  @click="openStatusModal(rowData.id, 'approved')"></i>
                        <i v-if="rowData.status === 'approved'" class="bi bi-x-circle text-warning hoverable-icon" v-tooltip="'Reject this Survey'"  @click="openStatusModal(rowData.id, 'rejected')"></i>

                        <i class="bi bi-trash text-danger hoverable-icon" v-tooltip="'Delete Survey'" @click="openDeleteModal(rowData.id)"></i>
                    </div>
                </td>
            </template>

        </EMDataTable>
    </div>

    <ConfirmationModal v-if="showDeleteModal" ref="deleteModal" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeDeleteModal" @perform-action="performDeleteAction"></ConfirmationModal>

    <ConfirmationModal v-if="showStatusModal" ref="statusModal" :content-id="'statusModalBlock'" :title="modal_title_text" :info_text="modal_info_text" :icon_class="modal_icon_class" :btn_text="modal_btn_text" :btn_color="modal_btn_color" @close-modal="closeStatusModal" @perform-action="performStatusAction"></ConfirmationModal>

</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import EMDataTable from '../../components/Table/EMDataTable.vue';
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import ConfirmationModal from '@/components/GenericModalAndViews/ConfirmationModal.vue';
import { useAuthStore } from "@/stores/auth-store";
import { useAppHelpers } from '@/composables/useAppHelpers';

const auth = useAuthStore();
const helper = useAppHelpers();

const isLoggedIn = computed(() => auth.isLoggedIn);
const isAdmin = computed(() => auth.isLoggedIn && auth.is_admin);

const adminTabItems = ref(null);
onMounted(() => {
    helper.loaderOn(adminTabItems.value);
});

const surveysTable = ref(null);
const columnDef = ref([
    {
        key: 'medication_name',
        label: 'Mediation',
        sortable: true,
        isSlot: true
    },
    {
        key: "type",
        label: "Type",
        sortable: true,
        formatter: (val, row) => val === 1 ? 'Type 1 - Present Experience' : (val === 2 ? 'Type 2 - From Past Experience' : '')
    },
    {
        key: "created_at",
        label: "Created At",
        sortable: true,
        formatter: (val, row) => helper.dateTimeFormatter(val)
    },
    {
        key: "status",
        label: "Status",
        class: "text-center",
        header_class: "text-center",
        sortable: true,
        isSlot: true
    },
    {
        key: "action",
        label: "Action(s)",
        header_class: "text-end",
        isSlot: true,
        hideCondition: isAdmin.value,
        width: 15
    }
]);

const modal_title_text = ref('');
const modal_info_text = ref('');
const modal_icon_class = ref('');
const modal_btn_text = ref('');
const modal_btn_color = ref('');

const deleteModal = ref(null);
const showDeleteModal = ref(false);
const deleteSurveyId = ref(null);
const openDeleteModal = (id) => {
    deleteSurveyId.value = id;
    modal_title_text.value = "Delete Survey";
    modal_info_text.value = "Are you sure you want to delete this survey!";
    modal_btn_text.value = "Yes, Delete It!";
    modal_icon_class.value = "bi-trash text-danger";
    modal_btn_color.value = 'btn-danger';
    showDeleteModal.value = true;
}
const closeDeleteModal = () => {
    deleteSurveyId.value = null;
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showDeleteModal.value = false;
}
const performDeleteAction = async () => {
    if (deleteSurveyId.value) {
        let url = "admin/surveys/delete/" + deleteSurveyId.value;
        try {
            let response = await apiClient.post(url, {});
            if (response.status === 200) {
                surveysTable.value.refresh();
                deleteModal.value.close();
            }
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
    }
    else {
        deleteModal.value.close();
    }
}

const statusModal = ref(null);
const showStatusModal = ref(false);
const statusSurveyId = ref(null);
const newSurveyStatus = ref('');
const openStatusModal = (id, status) => {
    statusSurveyId.value = id;
    newSurveyStatus.value = status;
    modal_title_text.value = "Approve Survey?";
    modal_info_text.value = "Are you sure you want to approve this survey!";
    modal_btn_text.value = "Yes, Approve it";
    modal_icon_class.value = "bi-check2-circle text-success";
    modal_btn_color.value = 'btn-success';
    if (newSurveyStatus.value === "rejected") {
        modal_title_text.value = "Reject Survey?";
        modal_info_text.value = "Are you sure you want to reject this survey!";
        modal_btn_text.value = "Yes, Reject it";
        modal_icon_class.value = "bi-x-circle text-warning";
        modal_btn_color.value = 'btn-warning';
    }
    showStatusModal.value = true;
}
const closeStatusModal = () => {
    statusSurveyId.value = null;
    newSurveyStatus.value = '';
    modal_title_text.value = "";
    modal_info_text.value = "";
    modal_btn_text.value = "";
    modal_icon_class.value = "";
    modal_btn_color.value = '';
    showStatusModal.value = false;
}
const performStatusAction = async () => {
    if (statusSurveyId.value) {
		let statusModalBlock = document.getElementById('statusModalBlock');
	    helper.loaderOn(statusModalBlock);
        let url = "admin/surveys/change_status/" + statusSurveyId.value;
        try {
            let response = await apiClient.post(url, {
				status: newSurveyStatus.value,
            });
            if (response.status === 200) {
                surveysTable.value.refresh();
            }
            statusModal.value.close();
        }
        catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
        }
		finally {
			helper.loaderOff(statusModalBlock);
        }
    }
    else {
        statusModal.value.close();
    }
}

</script>